import { FC } from 'react'
import ReactECharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

/** 本期用绿色，前30天均值用黄色 */
const LINE_COLOR = '#00CC00'
const REF_COLOR = '#ffb72b'

/** 线的面积渐变：色块顶部浅色，往下淡到透明 */
const areaGradient = (hex: string) => ({
	type: 'linear',
	x: 0,
	y: 0,
	x2: 0,
	y2: 1,
	colorStops: [
		{ offset: 0, color: `${hex}4D` },
		{ offset: 1, color: `${hex}00` }
	]
})

interface TrendChartProps {
	/** 每个数据点的横轴标签（时段或日期） */
	labels: string[]
	/** 本期数值 */
	data: number[]
	/** 前 30 天同时段均值，跟 data 一一对应 */
	refData: number[]
	/** 本期折线颜色，默认绿色；前30天均值线颜色固定不受此影响 */
	color?: string
}

/** 顶部三指标点进去看的趋势详情图：带时间轴 + 鼠标移入显示该时段数据，Sparkline 太简化撑不住这些交互，所以单独拆出来 */
const TrendChart: FC<TrendChartProps> = ({ labels, data, refData, color = LINE_COLOR }) => {
	const { t } = useTranslation()
	const currentName = t('c_a_voc_warning_trend_current')
	const refAvgName = t('c_a_voc_warning_trend_ref_avg')
	/** 系列名 -> 颜色，供 tooltip 自绘圆点用（本期折线的 itemStyle.color 是白色，直接用它当圆点会看不见） */
	const NAME_COLOR: Record<string, string> = { [currentName]: color, [refAvgName]: REF_COLOR }
	/** 按小时的横轴label是"00:00"这种带冒号的格式，小时数据点全部展示；
	 * 按天点位多的时候（超过21个）逐点label会挤在一起，才做隐藏 */
	const isHourly = labels[0]?.includes(':')
	const showPointLabel = isHourly || labels.length <= 21
	const option = {
		grid: { left: 8, right: 32, top: 32, bottom: 40, containLabel: true },
		tooltip: {
			trigger: 'axis',
			axisPointer: { type: 'line', lineStyle: { color: '#dee0e3' } },
			formatter: (params: any[]) => {
				const rows = params
					.map(
						p => `<div style="display:flex;align-items:center;gap:6px;margin-top:4px;">
							<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${
								NAME_COLOR[p.seriesName] ?? p.color
							};"></span>
							<span style="flex:1;">${p.seriesName}</span>
							<span style="font-weight:600;margin-left:12px;">${p.value}</span>
						</div>`
					)
					.join('')
				const axisLabel = params[0]?.axisValueLabel ?? params[0]?.name ?? ''
				return `<div style="font-weight:600;">${axisLabel}</div>${rows}`
			}
		},
		legend: {
			bottom: 6,
			itemWidth: 24,
			itemHeight: 10,
			itemGap: 24,
			textStyle: { color: '#646a73', fontSize: 12 }
		},
		xAxis: {
			type: 'category',
			data: labels,
			boundaryGap: false,
			axisTick: { show: false },
			axisLine: { lineStyle: { color: '#dee0e3' } },
			axisLabel: { color: '#8f959e', fontSize: 12 }
		},
		yAxis: {
			type: 'value',
			splitNumber: 2,
			splitLine: { lineStyle: { color: '#eceef0' } },
			axisLabel: { color: '#8f959e', fontSize: 12 }
		},
		series: [
			{
				name: currentName,
				type: 'line',
				data,
				symbol: 'circle',
				symbolSize: 5,
				showSymbol: true,
				lineStyle: { color, width: 2 },
				/** 空心圆点：填充色用白色、只描边，不要实心圆 */
				itemStyle: { color: '#fff', borderColor: color, borderWidth: 1 },
				areaStyle: { color: areaGradient(color) },
				label: {
					show: showPointLabel,
					position: 'top',
					color,
					fontSize: 11.5,
					formatter: (p: any) => (p.value ? p.value : '')
				}
			},
			{
				name: refAvgName,
				type: 'line',
				data: refData,
				symbol: 'circle',
				symbolSize: 5,
				showSymbol: false,
				lineStyle: {
					color: REF_COLOR,
					width: 1.5
					// type: 'dashed'
				},
				/** 图例图标跟「本期」保持同一套空心圆样式，只是不在线上逐点画出来 */
				itemStyle: { color: '#fff', borderColor: REF_COLOR, borderWidth: 1 },
				label: {
					show: showPointLabel,
					position: 'top',
					color,
					fontSize: 11.5,
					formatter: (p: any) => (p.value ? p.value : '')
				}
			}
		]
	}

	return <ReactECharts option={option} style={{ height: 350 }} notMerge />
}

export default TrendChart
